import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can } from '@pl/domain';
import type { GoogleSheetDto } from '@pl/domain';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { shortDateTime, zoneName, zonedDateTime } from '../../lib/format';
import { qk, useGoogleSheet, useMe, useOrg } from '../../lib/queries';
import { Chip, Empty, ErrorNote, RefetchNote, Spinner } from '../../ui/bits';
import { Modal } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { CsvDownloads } from '../CsvDownloads';
import s from './SettingsSheets.module.css';

/** Ticks every `ms`, so "40 seconds ago" keeps up between the page's 30 s refetches. */
function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/**
 * "40 seconds ago" and "12 min ago" while recent. After that, the time in the company's zone,
 * named as every labelled time is (`zoneName`, `zonedDateTime`): "today at 2:00 AM PT", "yesterday
 * at 2:00 AM PT", then "Sep 21, 2:00 AM PT". The dumps run at 02:00 company time, and an admin may
 * read this page from another continent.
 */
function when(iso: string, now: number, timeZone: string | undefined): string {
  const at = new Date(iso);
  const secs = Math.max(0, Math.round((now - at.getTime()) / 1000));
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs} seconds ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  try {
    const day = (d: Date) => d.toLocaleDateString('en-CA', { timeZone });
    const zone = zoneName(at, timeZone);
    const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone });
    if (day(at) === day(new Date(now))) return `today at ${time} ${zone}`.trimEnd();
    if (day(at) === day(new Date(now - 86_400_000))) return `yesterday at ${time} ${zone}`.trimEnd();
    return zonedDateTime(iso, timeZone);
  } catch {
    return shortDateTime(iso); // a zone this browser does not know
  }
}

/** The Sheet id in a pasted link, or the text itself if it is a bare id: only to compare with the one stored. */
const idIn = (text: string): string => /\/d\/([\w-]+)/.exec(text)?.[1] ?? text.trim();

/**
 * What the header chip says. Only what the server has said: a Sheet that is connected but whose
 * server has no service account is not being written, whatever its last write says.
 */
function status(g: GoogleSheetDto): { tone?: 'ok' | 'warn' | 'bad'; label: string } {
  if (!g.spreadsheetId) return { label: 'Not connected' };
  if (!g.enabled) return { tone: 'warn', label: 'Paused' };
  if (!g.serviceAccountEmail) return { tone: 'warn', label: 'Not writing' };
  if (g.lastError) return { tone: 'bad', label: 'Error' };
  return { tone: 'ok', label: 'Writing' };
}

type Confirm = 'refresh' | 'disconnect' | { kind: 'switch'; url: string };

/** Office → Settings → Google Sheets: connect the company's Sheet, see what is written to it, and download the same tables. */
export function SettingsSheets() {
  const meQuery = useMe();
  const me = meQuery.data;
  const canManage = me ? can(me.role, 'settings') : false;
  const sheetQuery = useGoogleSheet(canManage);
  // The company's zone, for the write times. Until it arrives, or if it can't, the viewer's own, named.
  const timeZone = useOrg().data?.timezone;

  if (!me) return <Spinner label="Loading Google Sheets" />;
  if (!canManage)
    return (
      <div className="panel">
        <Empty title="Only an admin can connect Google Sheets." description="An admin shares the company’s Google Sheet with this server and connects it here." />
      </div>
    );

  const sheet = sheetQuery.data;
  const chip = sheet ? status(sheet) : null;

  return (
    <div className={s.gsPage}>
      <div className="panel">
        <div className="panel-h">
          <h2>Google Sheet</h2>
          {chip && (
            <Chip tone={chip.tone} dot={Boolean(chip.tone)}>
              {chip.label}
            </Chip>
          )}
          <span className="grow" />
          <span className="sub">Live stock and the daily tables, written automatically</span>
        </div>
        <RefetchNote queries={[sheetQuery]} className={s.gsNote} />
        {sheetQuery.isLoadingError ? (
          <div className={s.gsNote}>
            <ErrorNote error={sheetQuery.error} />
            <button type="button" className="btn" onClick={() => sheetQuery.refetch()}>
              Try again
            </button>
          </div>
        ) : !sheet ? (
          <div className="panel-b">
            <Spinner label="Loading Google Sheets" />
          </div>
        ) : (
          <SheetPanel sheet={sheet} timeZone={timeZone} />
        )}
      </div>

      <p className={s.gsNever}>
        <Icon name="lock" />
        The platform only writes these tabs. It never reads them back, so anything you type in them is overwritten.
      </p>

      {can(me.role, 'export') && (
        <div className="panel">
          <div className="panel-h">
            <h2>Download as CSV</h2>
            <span className="grow" />
            <span className="sub">The same tables, as they are right now</span>
          </div>
          <CsvDownloads />
        </div>
      )}
    </div>
  );
}

function SheetPanel({ sheet, timeZone }: { sheet: GoogleSheetDto; timeZone: string | undefined }) {
  const qc = useQueryClient();
  const id = useId();
  const now = useNow(10_000);
  const [url, setUrl] = useState('');
  const [copied, setCopied] = useState<'yes' | 'select' | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [queued, setQueued] = useState(false);
  const address = useRef<HTMLElement>(null);

  const saved = (dto: GoogleSheetDto) => {
    qc.setQueryData(qk.googleSheet, dto);
    void qc.invalidateQueries({ queryKey: qk.googleSheet });
  };
  const connect = useMutation({
    mutationFn: (spreadsheetUrl: string) => api.put<GoogleSheetDto>('/integrations/google-sheet', { spreadsheetUrl }),
    onSuccess: (dto) => {
      saved(dto);
      setUrl('');
      setQueued(false);
      setConfirm(null);
    },
    onError: () => setConfirm(null),
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.put<GoogleSheetDto>('/integrations/google-sheet', { enabled }),
    onSuccess: (dto) => {
      saved(dto);
      setQueued(false);
      setConfirm(null);
    },
  });
  const refresh = useMutation({
    mutationFn: () => api.post<{ queued: string[] }>('/integrations/google-sheet/refresh'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.googleSheet });
      setQueued(true);
      setConfirm(null);
    },
  });

  // "Copied" for 2 s, then the button is a button again.
  useEffect(() => {
    if (copied !== 'yes') return;
    const t = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  async function copy(email: string) {
    try {
      await navigator.clipboard.writeText(email);
      setCopied('yes');
    } catch {
      // No clipboard (a page served over plain http has none): select it, so Ctrl+C does the rest.
      if (address.current) window.getSelection()?.selectAllChildren(address.current);
      setCopied('select');
    }
  }

  const hasSheet = Boolean(sheet.spreadsheetId);
  const account = sheet.serviceAccountEmail;
  const canConnect = Boolean(account) && url.trim().length > 0 && !connect.isPending;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!canConnect) return;
    // Pointing at another Sheet leaves the old one as it is, no longer written: say so first.
    if (hasSheet && idIn(url) !== sheet.spreadsheetId) setConfirm({ kind: 'switch', url: url.trim() });
    else connect.mutate(url.trim());
  }

  const sheetName = sheet.spreadsheetTitle ?? 'this Sheet';

  return (
    <>
      <div className={s.gs}>
        <div>
          <span className={s.k}>Sheet</span>
          {hasSheet ? (
            <>
              <span className={s.v}>
                <Icon name="sheet" />
                {/* The server's own link and title: nothing here is taken from what was pasted. */}
                <a className={s.gsTitle} href={sheet.spreadsheetUrl ?? undefined} target="_blank" rel="noopener noreferrer">
                  {sheet.spreadsheetTitle ?? <span className="mono">{sheet.spreadsheetId}</span>}
                </a>
              </span>
              <small>Last successful write: {sheet.lastSuccessAt ? when(sheet.lastSuccessAt, now, timeZone) : 'nothing written yet'}</small>
              <small>Live stock: {sheet.lastLiveStockAt ? when(sheet.lastLiveStockAt, now, timeZone) : 'not written yet'}</small>
              <small>Daily dumps: {sheet.lastDumpAt ? when(sheet.lastDumpAt, now, timeZone) : 'not written yet'}</small>
            </>
          ) : (
            <>
              <span className={s.v}>No Sheet connected yet</span>
              <small>Share your Sheet with this server, then paste its link below.</small>
            </>
          )}
        </div>
        <div>
          {account ? (
            <>
              <span className={s.k}>Share the Sheet with this address as Editor</span>
              <div className={s.email}>
                <span ref={address} className="mono">
                  {account}
                </span>
                <button type="button" className="btn sm" onClick={() => void copy(account)}>
                  <Icon name={copied === 'yes' ? 'check' : 'link'} />
                  {copied === 'yes' ? 'Copied' : 'Copy address'}
                </button>
              </div>
              {copied === 'select' && <small>Selected. Press Ctrl+C to copy it.</small>}
              <small>
                Open your Google Sheet → <b>Share</b> → paste this address → choose <b>Editor</b> → <b>Send</b>. Give warehouse staff Viewer access, never
                Editor.
              </small>
            </>
          ) : (
            <div className="callout">
              <span className="ic">
                <Icon name="alert" />
              </span>
              <div className={s.calloutText}>
                <b>This server has no Google service account installed yet.</b>
                <span>
                  Until it has one, it can’t write any Sheet. Ask whoever runs the server to follow <span className="mono">docs/runbook/google-sheets.md</span>,
                  “One-time setup”.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={s.gsBody}>
        {sheet.lastError && (
          <div className="callout bad" role="alert">
            <span className="ic">
              <Icon name="alert" />
            </span>
            <div className={s.calloutText}>
              <b>The last write failed</b>
              <span>{sheet.lastError}</span>
            </div>
          </div>
        )}

        {hasSheet && (
          <p className={s.gsCounting}>
            While a count is open, the Sheet shows <b>counting</b> for the SKU being counted. That’s normal: its numbers come back when the count is
            finished.
          </p>
        )}

        <form className={s.gsForm} onSubmit={submit} noValidate>
          <div className="fld">
            <label htmlFor={`${id}-url`}>{hasSheet ? 'Google Sheet link (to connect a different Sheet)' : 'Google Sheet link'}</label>
            <div className={s.gsRow}>
              <input
                id={`${id}-url`}
                className={`inp ${account ? '' : 'off'}`}
                type="url"
                inputMode="url"
                value={url}
                maxLength={500}
                disabled={!account}
                placeholder="https://docs.google.com/spreadsheets/d/…/edit"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (connect.isError) connect.reset();
                }}
              />
              <button type="submit" className="btn primary" disabled={!canConnect}>
                <Icon name="link" />
                {connect.isPending ? 'Checking access…' : 'Connect'}
              </button>
            </div>
          </div>
          {connect.isError && (
            // The API's own words: for a Sheet not shared, they already name the address to share with.
            <div className="callout bad" role="alert">
              <span className="ic">
                <Icon name="alert" />
              </span>
              <div className={s.calloutText}>
                <ErrorText error={connect.error} />
              </div>
            </div>
          )}
        </form>
      </div>

      <table className={`t ${s.gsTable}`} aria-label="Tabs this platform writes">
        <thead>
          <tr>
            <th>Tab</th>
            <th>Contents</th>
            <th>Written</th>
          </tr>
        </thead>
        <tbody>
          {sheet.tabs.map((t) => (
            <tr key={t.name}>
              <td className={s.tabn}>{t.name}</td>
              <td>{t.contents}</td>
              <td>{t.writtenWhen}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {hasSheet && (
        <div className={s.gf}>
          {sheet.enabled ? (
            <>
              <button type="button" className="btn sm" disabled={!account} onClick={() => setConfirm('refresh')}>
                <Icon name="refresh" />
                Write now
              </button>
              <button type="button" className="btn sm danger" onClick={() => setConfirm('disconnect')}>
                Disconnect
              </button>
            </>
          ) : (
            <button type="button" className="btn sm primary" disabled={!account || setEnabled.isPending} onClick={() => setEnabled.mutate(true)}>
              <Icon name="refresh" />
              Resume writing
            </button>
          )}
          {queued && (
            <span role="status" className={s.gfDone}>
              Queued. The tabs update within a minute.
            </span>
          )}
          {!queued && <span>Failed writes are tried again, with longer waits between tries.</span>}
          {!sheet.enabled && setEnabled.isError && <ErrorNote error={setEnabled.error} />}
        </div>
      )}

      {confirm === 'refresh' && (
        <Modal
          open
          onOpenChange={(o) => {
            if (!o) setConfirm(null);
          }}
          title="Write every tab now?"
          footer={
            <>
              <span className="grow" />
              <button type="button" className="btn ghost" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button type="button" className="btn primary" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
                Write now
              </button>
            </>
          }
        >
          {refresh.isError && <ErrorNote error={refresh.error} />}
          <p className={s.confirm}>Every tab is written again within a minute, even if nothing has changed, so anything typed into them is overwritten.</p>
        </Modal>
      )}
      {confirm === 'disconnect' && (
        <Modal
          open
          onOpenChange={(o) => {
            if (!o) setConfirm(null);
          }}
          title="Stop writing to this Sheet?"
          footer={
            <>
              <span className="grow" />
              <button type="button" className="btn ghost" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button type="button" className="btn danger" disabled={setEnabled.isPending} onClick={() => setEnabled.mutate(false)}>
                Disconnect
              </button>
            </>
          }
        >
          {setEnabled.isError && <ErrorNote error={setEnabled.error} />}
          <p className={s.confirm}>The tabs stay as they are and nothing is deleted.</p>
        </Modal>
      )}
      {typeof confirm === 'object' && confirm?.kind === 'switch' && (
        <Modal
          open
          onOpenChange={(o) => {
            if (!o) setConfirm(null);
          }}
          title="Connect a different Sheet?"
          footer={
            <>
              <span className="grow" />
              <button type="button" className="btn ghost" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button type="button" className="btn primary" disabled={connect.isPending} onClick={() => connect.mutate(confirm.url)}>
                {connect.isPending ? 'Checking access…' : 'Connect'}
              </button>
            </>
          }
        >
          <p className={s.confirm}>
            {sheet.spreadsheetTitle ? <b>{sheetName}</b> : 'The Sheet connected now'} stops updating. Nothing is deleted from it.
          </p>
        </Modal>
      )}
    </>
  );
}

/** An error's own message: the API's fixed phrases, or "No connection to the server". */
function ErrorText({ error }: { error: unknown }) {
  return <span>{error instanceof Error ? error.message : 'Something went wrong'}</span>;
}
